import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const TYPE_LABELS = {
  asset: 'Aktiver',
  liability: 'Gæld',
  equity: 'Egenkapital'
};

export default function OpeningBalance() {
  const { apiFetch } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [balances, setBalances] = useState({});
  const [fiscalYear, setFiscalYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    apiFetch('/api/opening-balance')
      .then(data => {
        setAccounts(data.accounts || []);
        setFiscalYear(data.fiscal_year || null);
        const bal = {};
        for (const b of (data.balances || [])) {
          bal[b.account_id] = b.amount;
        }
        setBalances(bal);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [apiFetch]);

  const setAmount = (accountId, value) => {
    setBalances(prev => ({ ...prev, [accountId]: value }));
  };

  const grouped = {
    asset: accounts.filter(a => a.type === 'asset'),
    equity: accounts.filter(a => a.type === 'equity'),
    liability: accounts.filter(a => a.type === 'liability'),
  };

  const totalAssets = accounts
    .filter(a => a.type === 'asset')
    .reduce((sum, a) => sum + (parseFloat(balances[a.id]) || 0), 0);

  const totalLiabilities = accounts
    .filter(a => a.type === 'liability')
    .reduce((sum, a) => sum + (parseFloat(balances[a.id]) || 0), 0);

  const totalEquity = accounts
    .filter(a => a.type === 'equity')
    .reduce((sum, a) => sum + (parseFloat(balances[a.id]) || 0), 0);

  const difference = totalAssets - totalLiabilities - totalEquity;
  const isBalanced = Math.abs(difference) < 0.01;

  const handleSave = async () => {
    setError('');
    setSuccess('');

    const balanceList = Object.entries(balances)
      .filter(([, val]) => { const n = parseFloat(val); return !isNaN(n) && n !== 0; })
      .map(([accountId, amount]) => ({
        account_id: parseInt(accountId),
        amount: parseFloat(amount)
      }));

    if (balanceList.length === 0) {
      setError('Angiv mindst én saldo.');
      return;
    }

    if (!isBalanced) {
      setError(`Aktiver (${totalAssets.toFixed(2)}) skal stemme med gæld + egenkapital (${(totalLiabilities + totalEquity).toFixed(2)}). Forskellen er ${Math.abs(difference).toFixed(2)} kr.`);
      return;
    }

    setSaving(true);
    try {
      await apiFetch('/api/opening-balance', {
        method: 'POST',
        body: { balances: balanceList }
      });
      setSuccess('Åbningsbalance er gemt!');
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  if (loading) return <div className="loading">Indlæser...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Åbningsbalance</h1>
      </div>

      {fiscalYear && (
        <div className="alert alert-success" style={{background: '#ebf8ff', color: '#2a4365', border: '1px solid #90cdf4', marginBottom: '1rem'}}>
          Regnskabsår: <strong>{fiscalYear.name}</strong> ({fiscalYear.start_date} — {fiscalYear.end_date})
        </div>
      )}

      <p style={{color: '#64748b', marginBottom: '1.5rem'}}>
        Indtast startsaldi for alle balancekonti. Aktiver skal stemme med gæld + egenkapital.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {Object.entries(grouped).map(([type, accs]) => (
        accs.length > 0 && (
          <div key={type} className="card" style={{marginBottom: '1.5rem'}}>
            <h3 style={{marginBottom: '1rem'}}>{TYPE_LABELS[type]}</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Kontonr.</th>
                    <th>Navn</th>
                    <th style={{textAlign: 'right', width: '180px'}}>Saldo (kr.)</th>
                  </tr>
                </thead>
                <tbody>
                  {accs.map(a => (
                    <tr key={a.id}>
                      <td>{a.account_number}</td>
                      <td>{a.name}</td>
                      <td style={{textAlign: 'right'}}>
                        <input
                          type="number"
                          className="form-control"
                          style={{textAlign: 'right', maxWidth: '180px', marginLeft: 'auto'}}
                          value={balances[a.id] || ''}
                          onChange={e => setAmount(a.id, e.target.value)}
                          placeholder="0,00"
                          step="0.01"
                        />
                      </td>
                    </tr>
                  ))}
                  <tr style={{fontWeight: 700, borderTop: '2px solid #e2e8f0'}}>
                    <td colSpan={2}>
                      Sum {TYPE_LABELS[type].toLowerCase()}
                    </td>
                    <td style={{textAlign: 'right'}}>
                      {type === 'asset' ? totalAssets.toFixed(2) :
                       type === 'liability' ? totalLiabilities.toFixed(2) :
                       totalEquity.toFixed(2)} kr.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )
      ))}

      <div className="card" style={{marginBottom: '1.5rem', padding: '1.25rem'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', fontWeight: 600}}>
          <span>Aktiver i alt:</span>
          <span>{totalAssets.toFixed(2)} kr.</span>
        </div>
        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', fontWeight: 600, marginTop: '0.5rem'}}>
          <span>Gæld + Egenkapital i alt:</span>
          <span>{(totalLiabilities + totalEquity).toFixed(2)} kr.</span>
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: '1.1rem', fontWeight: 700, marginTop: '0.75rem',
          paddingTop: '0.75rem', borderTop: '2px solid #e2e8f0',
          color: isBalanced ? '#16a34a' : '#dc2626'
        }}>
          <span>Difference:</span>
          <span>{difference.toFixed(2)} kr. {isBalanced ? '✓ Stemmer' : '✗ Stemmer ikke'}</span>
        </div>
      </div>

      <button
        className="btn btn-primary"
        style={{width: '100%', justifyContent: 'center', padding: '0.85rem'}}
        onClick={handleSave}
        disabled={saving || !isBalanced}
      >
        {saving ? 'Gemmer...' : 'Gem åbningsbalance'}
      </button>
    </div>
  );
}
